/**
 * Background Schedule — create / edit, as its own PAGE.
 *
 * Moved out of the Schedules tab modal for the same reason as the job form: a
 * route of its own plus `useNavigationBlocker` + `DiscardChangesDialog`, so a
 * half-filled schedule cannot be lost to a stray backdrop click.
 *
 * There is no GET /schedules/{id} endpoint — only GET /{jobId}/schedules — so the
 * edit route carries `?jobId=` and loads that job's schedules to find this one. If
 * the query param is missing (a hand-typed URL, an old bookmark) it falls back to
 * scanning every job's schedules, which is what the list tab does anyway.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'sharedUi/Calendar';
import { InputSwitch } from 'sharedUi/InputSwitch';
import { Input } from 'sharedUi/Input';
import { Dropdown } from 'sharedUi/Dropdown';
import { Button } from 'sharedUi/Button';
import { Message } from 'sharedUi/Message';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { backgroundJobService } from '../../services/background-job.service';
import type {
  BackgroundJobSchedule, BackgroundJobScheduleCreate, ScheduleType,
} from '../../types/background-job.types';
import { POLL_INTERVAL_SECONDS, blankTiming, fromNaiveIso, toNaiveIso } from './tabs/shared';
import { JsonField } from '../../components/common/json-field';
import FormField from '../../components/common/form-field';
import { ProjectCell } from '../../components/common/project-cell';
import { useAccessibleProjects } from '../../hooks/useProjectScope';
import { useToastApi } from '../../hooks/use-toast-api';
import { useScheduleTypes } from './tabs/use-job-value-sets';
import { useJobPermissions } from './tabs/use-job-permissions';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import ScreenHeader from '../../components/common/screen-header';

const LIST_ROUTE = '/background-jobs?tab=schedules';

const EMPTY: BackgroundJobScheduleCreate = {
  scheduleName: 'default',
  scheduleType: 'CRON',
  cronExpression: '0 2 * * *',
  isScheduleEnabled: true,
  scheduleParameters: {},
};

const ScheduleFormPage = () => {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const isEditMode = !!id;
  const { canCreate, canUpdate } = useJobPermissions();
  const qc = useQueryClient();
  const { toast, ok, fail } = useToastApi();
  const { options: scheduleTypeOptions } = useScheduleTypes();

  const [jobId, setJobId] = useState(params.get('jobId') || '');
  const [form, setForm] = useState<BackgroundJobScheduleCreate>(EMPTY);
  const [editing, setEditing] = useState<BackgroundJobSchedule | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const initialRef = useRef<string>(JSON.stringify(EMPTY));
  const savedRef = useRef(false);

  const jobsQ = useQuery({
    queryKey: ['bg-jobs', 'all-for-schedules'],
    queryFn: () => backgroundJobService.listJobs({ page: 1, page_size: 200 }),
    staleTime: 60_000,
  });
  const jobs = useMemo(() => jobsQ.data?.items ?? [], [jobsQ.data]);

  /* Which project this schedule belongs to.
     Derived, never chosen: a background job schedule carries no appId of its own
     — background_job_dashboard_service notes that "schedules and runs carry no
     project of their own, so they are scoped through the job ids" — so the job
     above decides, and this shows what that decision is before saving. */
  const { projects, canScopeByProject } = useAccessibleProjects();
  const jobAppId = useMemo(
    () => jobs.find((j) => j._id === jobId)?.appId ?? null,
    [jobs, jobId],
  );

  // Edit: load the owning job's schedules (or every job's, if jobId was not passed).
  const hintedJobId = params.get('jobId') || '';
  const lookupJobs = useMemo(() => {
    if (!isEditMode) return [];
    if (hintedJobId) return jobs.filter((j) => j._id === hintedJobId);
    return jobs;
  }, [isEditMode, hintedJobId, jobs]);

  const scheduleQs = useQueries({
    queries: lookupJobs.map((j) => ({
      queryKey: ['bg-schedules', j._id],
      queryFn: () => backgroundJobService.listSchedules(j._id),
      staleTime: 30_000,
    })),
  });

  const found = useMemo(
    () => scheduleQs.flatMap((q) => q.data ?? []).find((s) => s._id === id) ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scheduleQs.map((q) => q.dataUpdatedAt).join(','), id],
  );

  useEffect(() => {
    if (!found) return;
    const loaded: BackgroundJobScheduleCreate = {
      scheduleName: found.scheduleName,
      scheduleType: found.scheduleType,
      cronExpression: found.cronExpression || undefined,
      intervalSeconds: found.intervalSeconds || undefined,
      runAtDateTime: found.runAtDateTime || undefined,
      startDateTime: found.startDateTime || undefined,
      endDateTime: found.endDateTime || undefined,
      isScheduleEnabled: found.isScheduleEnabled,
      scheduleParameters: found.scheduleParameters || {},
    };
    setEditing(found);
    setJobId(found.jobId);
    setForm(loaded);
    initialRef.current = JSON.stringify(loaded);
  }, [found]);

  // On create, default to the first job so the form is usable immediately.
  useEffect(() => {
    if (!isEditMode && !jobId && jobs.length) setJobId(jobs[0]._id);
  }, [isEditMode, jobId, jobs]);

  const isDirty = !savedRef.current && JSON.stringify(form) !== initialRef.current;
  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } =
    useNavigationBlocker({ when: isDirty });

  const saveM = useMutation({
    mutationFn: (body: BackgroundJobScheduleCreate) => (isEditMode
      ? backgroundJobService.updateSchedule(id!, body)
      : backgroundJobService.createSchedule(jobId, body)),
    onSuccess: () => {
      ok(isEditMode ? 'Schedule updated' : 'Schedule created');
      savedRef.current = true;
      qc.invalidateQueries({ queryKey: ['bg-schedules'] });
      qc.invalidateQueries({ queryKey: ['bg-dashboard'] });
      handleNavigate(LIST_ROUTE);
    },
    onError: (e) => fail(e),
  });

  /** Timing fields are mutually exclusive — switching type clears the other two. */
  const changeType = (scheduleType: ScheduleType) =>
    setForm((f) => ({ ...f, scheduleType, ...blankTiming(scheduleType) }));

  /* Mirrors the server validators so a bad value is caught before the round-trip
     rather than coming back as a raw 422. */
  const timingError = useMemo(() => {
    if (form.scheduleType === 'CRON' && !form.cronExpression?.trim()) return 'A cron expression is required.';
    if (form.scheduleType === 'INTERVAL') {
      if (!form.intervalSeconds) return 'An interval is required.';
      if (form.intervalSeconds < POLL_INTERVAL_SECONDS) {
        return `The interval must be at least the engine poll interval (${POLL_INTERVAL_SECONDS}s) — anything shorter cannot fire on time.`;
      }
    }
    if (form.scheduleType === 'ONCE') {
      if (!form.runAtDateTime) return 'A run-at date and time is required.';
      const at = fromNaiveIso(form.runAtDateTime);
      if (at && at.getTime() <= Date.now()) return 'The run-at time must be in the future.';
    }
    const start = fromNaiveIso(form.startDateTime);
    const end = fromNaiveIso(form.endDateTime);
    if (start && end && end.getTime() <= start.getTime()) return 'The end date must be after the start date.';
    return '';
  }, [form]);

  /** a ONCE schedule that has fired: it disabled itself and needs both fields reset */
  const isSpentOnce = !!editing && editing.scheduleType === 'ONCE'
    && !!editing.lastRunAt && !editing.isScheduleEnabled;

  const invalid = !jobId || !form.scheduleName?.trim() || !!timingError;

  const submit = () => {
    setSubmitted(true);
    if (invalid) return;
    // send only this type's timing field; the other two must be absent
    const { cronExpression, intervalSeconds, runAtDateTime, ...rest } = form;
    saveM.mutate({
      ...rest,
      ...(form.scheduleType === 'CRON' ? { cronExpression } : {}),
      ...(form.scheduleType === 'INTERVAL' ? { intervalSeconds } : {}),
      ...(form.scheduleType === 'ONCE' ? { runAtDateTime } : {}),
    });
  };

  const allowed = isEditMode ? canUpdate : canCreate;
  if (!allowed) {
    return (
      <div className="py-4 px-4 lg:px-6">
        <Message severity="warn" className="w-full justify-start"
          text="You do not have permission to open this form." />
      </div>
    );
  }

  return (
    <>
      {toast}
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900 min-h-screen" data-testid="SCR-BackgroundSchedule-Form">
        <ScreenHeader
          icon="pi pi-calendar"
          title={isEditMode ? `Edit ${form.scheduleName || 'schedule'}` : 'New schedule'}
          subtitle={isEditMode ? 'Update when this job runs' : 'Decide when a job should run'}
          onBack={() => handleNavigate(LIST_ROUTE)}
        />

        <div className="rounded border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="grid grid-cols-12 items-start gap-x-4 gap-y-4">
            {/* Enabled sits beside the job: the two facts an operator reads first are
                "which job is this for" and "is it actually on". */}
            <FormField className="col-span-9" label="Job" required
              hint={isEditMode ? 'A schedule cannot be moved to another job.' : undefined}>
              <Dropdown value={jobId} options={jobs.map((j) => ({ label: `${j.jobName} (${j.jobKey})`, value: j._id }))}
                optionLabel="label" optionValue="value" className="w-full" filter resetFilterOnHide
                disabled={isEditMode}
                onChange={(e: { value: string }) => setJobId(e.value)} />
            </FormField>

            {canScopeByProject && (
              <FormField className="col-span-9" label="Project"
                hint="Taken from the job above — a schedule is scoped through the job it runs.">
                <div className="flex h-[38px] items-center">
                  {jobId
                    ? <ProjectCell appId={jobAppId} projects={projects} />
                    : <span className="text-sm text-gray-400">Pick a job first.</span>}
                </div>
              </FormField>
            )}

            <FormField className="col-span-3" label="Enabled"
              hint="Re-enabling recomputes the next run from now.">
              <div className="flex h-[38px] items-center">
                <InputSwitch checked={form.isScheduleEnabled ?? true}
                  onChange={(e) => setForm((f) => ({ ...f, isScheduleEnabled: !!e.value }))} />
              </div>
            </FormField>

            <FormField className="col-span-12 sm:col-span-6" label="Schedule name" required
              hint="Unique per job and type — a job can hold several.">
              <Input name="scheduleName" value={form.scheduleName || ''} className="w-full"
                placeholder="nightly" onChange={(v: string) => setForm((f) => ({ ...f, scheduleName: v }))} />
            </FormField>
            <FormField className="col-span-12 sm:col-span-6" label="Type" required
              hint="Changing this clears the timing below.">
              <Dropdown value={form.scheduleType} options={scheduleTypeOptions}
                optionLabel="label" optionValue="value" className="w-full"
                onChange={(e: { value: ScheduleType }) => changeType(e.value)} />
            </FormField>

            <div className="col-span-12 rounded-md border border-gray-200 p-3 dark:border-gray-700">
              {form.scheduleType === 'CRON' && (
                <FormField label="Cron expression" required
                  hint={<>min hour day month weekday — <code>0 2 * * *</code> is 2am daily.</>}>
                  <Input name="cronExpression" value={form.cronExpression || ''} className="w-full"
                    placeholder="0 2 * * *" onChange={(v: string) => setForm((f) => ({ ...f, cronExpression: v }))} />
                </FormField>
              )}
              {form.scheduleType === 'INTERVAL' && (
                <FormField label="Every (seconds)" required
                  hint="Shorter than the engine poll interval will not fire any faster.">
                  <InputNumber value={form.intervalSeconds ?? 300} min={POLL_INTERVAL_SECONDS} showButtons
                    className="w-full num-compact" useGrouping={false}
                    onValueChange={(e) => setForm((f) => ({ ...f, intervalSeconds: e.value ?? undefined }))} />
                </FormField>
              )}
              {form.scheduleType === 'ONCE' && (
                <FormField label="Run at" required
                  hint="Fires once, then disables itself — shown as “Completed”.">
                  {/* No minDate on purpose. The picker validates every keystroke against
                      minDate and silently discards anything earlier, so editing a schedule
                      whose time has already passed became impossible. Past times are
                      caught by `timingError` below instead, which says why. */}
                  <Calendar value={fromNaiveIso(form.runAtDateTime)} showTime showIcon hourFormat="24"
                    dateFormat="dd M yy" className="w-full"
                    appendTo={typeof document !== 'undefined' ? document.body : undefined}
                    onChange={(e) => setForm((f) => ({ ...f, runAtDateTime: toNaiveIso((e.value as Date) ?? null) }))} />
                </FormField>
              )}
              {/* Shown straight away when editing, not just after a failed submit: an
                  existing schedule can already hold a time that has passed, and the
                  reason it will not fire should be on screen before the save is tried. */}
              {(submitted || isEditMode) && timingError
                && <div className="mt-2 text-xs text-red-500">{timingError}</div>}
            </div>

            <FormField className="col-span-12 sm:col-span-6" label="Start (optional)" hint="Before this, the schedule is dormant.">
              <Calendar value={fromNaiveIso(form.startDateTime)} showTime showIcon hourFormat="24" dateFormat="dd M yy"
                className="w-full" appendTo={typeof document !== 'undefined' ? document.body : undefined}
                onChange={(e) => setForm((f) => ({ ...f, startDateTime: toNaiveIso((e.value as Date) ?? null) }))} />
            </FormField>
            <FormField className="col-span-12 sm:col-span-6" label="End (optional)" hint="After this, it stops firing for good.">
              <Calendar value={fromNaiveIso(form.endDateTime)} showTime showIcon hourFormat="24" dateFormat="dd M yy"
                className="w-full" appendTo={typeof document !== 'undefined' ? document.body : undefined}
                onChange={(e) => setForm((f) => ({ ...f, endDateTime: toNaiveIso((e.value as Date) ?? null) }))} />
            </FormField>

            {/* A fired ONCE schedule switches itself off. Setting a new time alone leaves it
                correctly scheduled but disabled, so both have to go in the same save. */}
            {isSpentOnce && (
              <Message className="col-span-12 w-full justify-start" severity="warn"
                text={`This one-off already ran on ${formatTimestamp(editing?.lastRunAt)} and switched itself off. To reuse it, set a future Run at and turn Enabled back on in this same save.`} />
            )}

            <JsonField className="col-span-12" label="Schedule parameters"
              hint="Overrides the job's defaults for this schedule only."
              value={form.scheduleParameters}
              onChange={(o) => setForm((f) => ({ ...f, scheduleParameters: o || {} }))} />
          </div>

          <div className="mt-4 flex justify-end gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
            <Button label="Cancel" severity="secondary" outlined onClick={() => handleNavigate(LIST_ROUTE)} />
            <Button label={isEditMode ? 'Save' : 'Create'} icon="pi pi-check"
              onClick={submit} disabled={saveM.isPending} />
          </div>
        </div>
      </div>

      <DiscardChangesDialog visible={showLeaveDialog} onStay={cancelLeave} onLeave={confirmLeave}
        testId="BackgroundScheduleForm.Dialog.Leave" />
    </>
  );
};

export default ScheduleFormPage;
